import { Link } from "react-router-dom";
import Reveal from "@/components/base/Reveal";
import { whatsappLink } from "@/constants/site";
import type { SolucaoService } from "@/mocks/solucoes";

interface ServiceShowcaseProps {
  service: SolucaoService;
  reverse?: boolean;
  alt?: boolean;
}

const ACCENT: Record<
  SolucaoService["accent"],
  { badge: string; icon: string; check: string }
> = {
  primary: {
    badge: "bg-primary-50 text-primary-700",
    icon: "bg-primary-100/70 text-primary-600",
    check: "text-primary-600",
  },
  accent: {
    badge: "bg-accent-50 text-accent-700",
    icon: "bg-accent-100/70 text-accent-700",
    check: "text-accent-600",
  },
  secondary: {
    badge: "bg-secondary-50 text-secondary-700",
    icon: "bg-secondary-100/70 text-secondary-700",
    check: "text-secondary-600",
  },
};

function ServiceVisual({ service }: { service: SolucaoService }) {
  const acc = ACCENT[service.accent];

  if (service.image) {
    return (
      <div className="overflow-hidden rounded-2xl border border-background-200 bg-background-100">
        <img
          src={service.image}
          alt={service.imageAlt ?? service.title}
          title={`${service.title} — NXHOST`}
          decoding="async"
          className="h-full w-full object-cover object-top"
        />
      </div>
    );
  }

  return (
    <div className="relative flex aspect-[4/3] w-full flex-col justify-between overflow-hidden rounded-2xl border border-background-200 bg-background-50 p-6 md:p-8">
      <i
        className={`${service.icon} pointer-events-none absolute -right-4 -top-8 text-[10rem] leading-none text-foreground-950/[0.04]`}
        aria-hidden="true"
      ></i>

      <span
        className={`relative flex h-16 w-16 items-center justify-center rounded-2xl ${acc.icon}`}
      >
        <i className={`${service.icon} text-3xl`} aria-hidden="true"></i>
      </span>

      <div className="relative">
        {service.visualEmail ? (
          <div className="mb-4 flex items-center gap-3 rounded-xl border border-background-200 bg-background-100 p-4">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
              <i className="ri-mail-line text-xl" aria-hidden="true"></i>
            </span>
            <div className="min-w-0">
              <p className="text-[11px] text-foreground-500">E-mail profissional</p>
              <p className="truncate text-sm font-semibold text-foreground-950">
                {service.visualEmail}
              </p>
            </div>
          </div>
        ) : null}

        <div className="flex flex-wrap gap-2">
          {service.highlights.slice(0, 4).map((item) => (
            <span
              key={item}
              className="rounded-full border border-background-200 bg-background-100 px-3 py-1 text-[11px] text-foreground-600"
            >
              {item}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ServiceShowcase({ service, reverse = false, alt = false }: ServiceShowcaseProps) {
  const acc = ACCENT[service.accent];

  return (
    <section
      id={service.id}
      className={`${alt ? "bg-background-100" : "bg-background-50"} scroll-mt-24 py-14 md:py-20`}
    >
      <div className="mx-auto max-w-7xl px-4 md:px-8">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <Reveal className={reverse ? "lg:order-2" : "lg:order-1"}>
            <ServiceVisual service={service} />
          </Reveal>

          <Reveal delay={120} className={reverse ? "lg:order-1" : "lg:order-2"}>
            <div>
              <span
                className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-wide ${acc.badge}`}
              >
                <i className={service.icon} aria-hidden="true"></i>
                {service.badge}
              </span>
              <h3 className="mt-5 font-heading text-2xl font-bold tracking-tight text-foreground-950 md:text-3xl">
                {service.title}
              </h3>
              <p className="mt-4 text-base leading-relaxed text-foreground-600">
                {service.description}
              </p>

              <ul className="mt-6 grid grid-cols-1 gap-x-6 gap-y-2.5 sm:grid-cols-2">
                {service.highlights.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm text-foreground-700">
                    <i className={`ri-check-line mt-0.5 shrink-0 ${acc.check}`} aria-hidden="true"></i>
                    {item}
                  </li>
                ))}
              </ul>

              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href={whatsappLink(service.ctaMessage)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  {service.ctaLabel}
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </a>
                <Link
                  to={service.href ?? "/contato"}
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-6 py-3.5 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  <i className="ri-customer-service-2-line text-lg" aria-hidden="true"></i>
                  {service.href ? "Saiba mais" : "Solicitar orçamento"}
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}